// light-theme.ts
import { createTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
    signature,
    neutral,
    shadows,
    radius,
    typography,
    signatureBorderStyleButtons
} from "./theme-tokens";

const lightFocusRing = (backgroundColor: string) => ({
    outline: `3px solid ${signature[900]}`,
    outlineOffset: 2,
    backgroundColor,
});

const lightBrand = {
    main: "#DA9C23",
    light: "#F1C756",
    dark: "#825D15",
    readable: signature[700],
    readableStrong: signature[900],
};

const lightSurfaces = {
    default: signature[50],
    paper: "#FFFCF4",
    subtle: "#FFF3DD",
};

const accessibleSupport = {
    breakout: { main: "#8B2D5E", light: "#98285F", dark: "#6F183F", contrastText: neutral[0] },
    error: { main: "#8E0000", light: "#A31515", dark: "#5C0000", contrastText: neutral[0] },
    warning: { main: "#7A4700", light: "#7B4900", dark: "#5E3A00", contrastText: neutral[0] },
    info: { main: "#01579B", light: "#005A99", dark: "#003C6D", contrastText: neutral[0] },
    success: { main: "#1B5E20", light: "#246526", dark: "#124216", contrastText: neutral[0] },
};

const light = createTheme({
    layout: {
        mobile: {
            menu: {
                drawer: {
                    width: '90vw'
                }
            }
        }
    },
    palette: {
        mode: "light",
        contrastThreshold: 7,
        primary: {
            main: lightBrand.main,
            light: lightBrand.light,
            dark: lightBrand.dark,
            contrastText: neutral[1000],
        },
        secondary: {
            main: neutral[800],
            light: neutral[700],
            dark: neutral[900],
            contrastText: neutral[0],
        },
        breakout: accessibleSupport.breakout,
        error: accessibleSupport.error,
        warning: accessibleSupport.warning,
        info: accessibleSupport.info,
        success: accessibleSupport.success,
        background: {
            default: lightSurfaces.default,
            paper: lightSurfaces.paper,
            dark: lightSurfaces.subtle
        },
        text: {
            primary: neutral[900],
            secondary: neutral[700],
            disabled: alpha(neutral[900], 0.55),
        },
        divider: alpha(neutral[900], 0.24),
        action: {
            active: neutral[900],
            hover: alpha(neutral[900], 0.06),
            selected: alpha(neutral[900], 0.10),
            disabled: alpha(neutral[900], 0.45),
            disabledBackground: alpha(neutral[900], 0.18),
            focus: alpha(neutral[900], 0.12),
        },
    },
    // @ts-expect-error - ignore
    typography,
    shape: { borderRadius: radius.sm },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { backgroundColor: lightSurfaces.default, color: neutral[900] },
                '*:focus-visible': lightFocusRing(lightSurfaces.default),
                '@media (prefers-reduced-motion: reduce)': {
                    '*': { animationDuration: '0.01ms !important', animationIterationCount: '1 !important', transitionDuration: '0.01ms !important', scrollBehavior: 'auto !important' },
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&.Mui-focusVisible': lightFocusRing(lightSurfaces.default),
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: alpha(neutral[900], 0.5)
                }
            }
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    subtitle1: 'p',
                    subtitle2: 'p',
                },
            },
            styleOverrides: {
                h1: { color: lightBrand.readable },
                h2: { color: lightBrand.readable },
                h3: { color: lightBrand.readable },
                h4: { color: lightBrand.readable },
                h5: { color: lightBrand.readable },
                h6: { color: lightBrand.readable },
                body1: { color: neutral[900] },
                body2: { color: neutral[900] },
                subtitle1: { color: neutral[700] },
                subtitle2: { color: lightBrand.readable },
                caption: { color: neutral[700] },
                overline: { color: neutral[700] },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: lightSurfaces.paper,
                    color: neutral[900],
                    backdropFilter: 'blur(5px)',
                    borderRadius: '8px',
                    '& .MuiTypography-body1, & .MuiTypography-body2': {
                        color: neutral[900],
                    },
                    '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2, & .MuiTypography-caption, & .MuiTypography-overline': {
                        color: neutral[700],
                    },
                    '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
                        color: lightBrand.readable,
                    },
                    '& .MuiTypography-colorTextSecondary': {
                        color: neutral[700],
                    },
                    '& .MuiLink-root': {
                        color: lightBrand.readable,
                        borderBottom: `1px dashed ${alpha(lightBrand.readable, .55)}`,
                        '&:hover': {
                            color: lightBrand.readableStrong,
                            borderBottom: `1px solid ${alpha(lightBrand.readableStrong, .9)}`,
                        },
                        '&:focus-visible': lightFocusRing(lightSurfaces.paper),
                    },
                },
                outlined: { borderColor: alpha(neutral[900], 0.24) },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: { boxShadow: shadows.level2(neutral[0]) },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    paddingLeft: '25px',
                    paddingRight: '25px',
                    ...signatureBorderStyleButtons,
                },
                containedPrimary: { color: "#000000" },
                containedSecondary: { color: "#FFFFFF" },
                outlinedPrimary: {
                    color: lightBrand.readable,
                    borderColor: lightBrand.readable,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: lightBrand.readableStrong,
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: lightBrand.readable,
                    textUnderlineOffset: 3,
                    textDecoration: 'none',
                    borderBottom: `1px dashed ${alpha(lightBrand.readable, .55)}`,
                    transition: 'color 0.3s ease, border-bottom-color 0.3s ease',
                    '&:hover': {
                        color: lightBrand.readableStrong,
                        borderBottom: `1px solid ${alpha(lightBrand.readableStrong, .9)}`,
                    },
                    '&:focus-visible': lightFocusRing(lightSurfaces.default),
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: neutral[900],
                    color: neutral[50],
                    ...(typography.body1)
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: lightSurfaces.paper,
                    color: neutral[900],
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                }
            }
        }
    },
});

export default light;
